/**
 * New-project modal.
 *
 * Wireframe B: keep the fast intake fields visible, then group optional
 * contract/indent, commercial/BG, and logistics fields in collapsible
 * sections. GST amount and declared total contract value are preview-only;
 * the server derives them from Total Store Value + GST %.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  addMonthsClamped,
  deriveDeclaredTotalContractValuePaisa,
  deriveGstAmountPaisa,
  formatBpsAsPercent,
  formatCurrency,
  normaliseName,
  normaliseProjectNumber,
  parsePercentInputToBps,
  parseRupeeInputToPaisa,
  TermsOfDelivery,
  todayPktIsoDate,
} from '@procurement/shared';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { FormField, Input, Textarea } from '@/components/ui/form-field';
import { Modal } from '@/components/ui/modal';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Select } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import {
  useClientsList,
  useCreateProject,
  type ProjectCurrencyCode,
  type ProjectInspectionLocation,
  type ProjectTermsOfDelivery,
} from '@/lib/project-queries';
import {
  buildCreateProjectPayload,
  hasCompleteForeignProjectDetails,
  type NewProjectFormState,
} from '@/lib/new-project-payload';
import { useIncotermsListAll } from '@/lib/masters-queries';
import { useDiscardDraft, useDraftsList, useSaveDraft } from '@/lib/draft-queries';

interface NewProjectModalProps {
  open: boolean;
  onClose: () => void;
}

function draftString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function draftBoolean(value: unknown): boolean {
  return typeof value === 'boolean' ? value : false;
}

function isProjectCreateDraftPayload(value: unknown): value is { form: Partial<NewProjectFormState> } {
  return Boolean(value && typeof value === 'object' && 'form' in value);
}

export function NewProjectModal({ open, onClose }: NewProjectModalProps) {
  const navigate = useNavigate();
  const clients = useClientsList();
  const createProject = useCreateProject();
  const incoterms = useIncotermsListAll();
  const projectDrafts = useDraftsList({ draftType: 'PROJECT_CREATE' }, open);
  const saveDraft = useSaveDraft();
  const discardDraft = useDiscardDraft();

  const [projectTitle, setProjectTitle] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [clientId, setClientId] = useState('');
  const [projectCurrency, setProjectCurrency] = useState<ProjectCurrencyCode>('PKR');
  const [otherCurrencyCode, setOtherCurrencyCode] = useState('');
  const [principalName, setPrincipalName] = useState('');
  const [principalAddress, setPrincipalAddress] = useState('');
  const [principalContactPerson, setPrincipalContactPerson] = useState('');
  const [principalEmail, setPrincipalEmail] = useState('');
  const [principalPhone, setPrincipalPhone] = useState('');
  const [principalFax, setPrincipalFax] = useState('');
  const [portOfShipment, setPortOfShipment] = useState('');
  const [principalBankName, setPrincipalBankName] = useState('');
  const [principalBankAccountNumber, setPrincipalBankAccountNumber] = useState('');
  const [principalBankIban, setPrincipalBankIban] = useState('');
  const [principalBankAccountTitle, setPrincipalBankAccountTitle] = useState('');
  const [principalBankSwiftCode, setPrincipalBankSwiftCode] = useState('');
  const [principalBankAddress, setPrincipalBankAddress] = useState('');
  const [principalBankEmail, setPrincipalBankEmail] = useState('');
  const [principalBankPhone, setPrincipalBankPhone] = useState('');
  const [principalBankFax, setPrincipalBankFax] = useState('');
  const [dfaReceivedDate, setDfaReceivedDate] = useState(todayPktIsoDate());
  const [contractSignDate, setContractSignDate] = useState('');
  const [deliveryDurationMonths, setDeliveryDurationMonths] = useState('');
  const [gracePeriodDays, setGracePeriodDays] = useState('15');
  const [warrantyPeriodMonths, setWarrantyPeriodMonths] = useState('');

  const [origin, setOrigin] = useState('');
  const [indentNumber, setIndentNumber] = useState('');
  const [indentDate, setIndentDate] = useState('');
  const [makerNameBrand, setMakerNameBrand] = useState('');
  const [termsOfDelivery, setTermsOfDelivery] = useState<'' | ProjectTermsOfDelivery>('');

  const [totalStoreValueInput, setTotalStoreValueInput] = useState('');
  const [gstPercentInput, setGstPercentInput] = useState('');
  const [requiredBgAmountInput, setRequiredBgAmountInput] = useState('');
  const [bgSubmissionDurationDays, setBgSubmissionDurationDays] = useState('');
  const [bgValidityDurationMonths, setBgValidityDurationMonths] = useState('');
  const [paymentOnINotePercentInput, setPaymentOnINotePercentInput] = useState('');
  const [paymentOnCrvPercentInput, setPaymentOnCrvPercentInput] = useState('');
  const [partialDeliveryAllowed, setPartialDeliveryAllowed] = useState(false);
  const [partialPaymentAllowed, setPartialPaymentAllowed] = useState(false);
  const [partialDeliveryPaymentRemarks, setPartialDeliveryPaymentRemarks] = useState('');

  const [earnestMoneyInput, setEarnestMoneyInput] = useState('');
  const [consigneeLocation, setConsigneeLocation] = useState('');
  const [defaultInspectionLocation, setDefaultInspectionLocation] = useState<ProjectInspectionLocation>('OUR_PREMISES');
  const [inspectionAuthority, setInspectionAuthority] = useState('');
  const [ndcRequired, setNdcRequired] = useState(false);
  const [ndcReferenceNumber, setNdcReferenceNumber] = useState('');
  const [ndcRemarks, setNdcRemarks] = useState('');
  const [remarks, setRemarks] = useState('');

  const latestProjectDraft = projectDrafts.data?.items[0] ?? null;

  const currentFormState = (): NewProjectFormState => ({
    projectTitle,
    projectNumber,
    clientId,
    projectCurrency,
    otherCurrencyCode,
    principalName,
    principalAddress,
    principalContactPerson,
    principalEmail,
    principalPhone,
    principalFax,
    portOfShipment,
    principalBankName,
    principalBankAccountNumber,
    principalBankIban,
    principalBankAccountTitle,
    principalBankSwiftCode,
    principalBankAddress,
    principalBankEmail,
    principalBankPhone,
    principalBankFax,
    dfaReceivedDate,
    contractSignDate,
    deliveryDurationMonths,
    gracePeriodDays,
    warrantyPeriodMonths,
    origin,
    indentNumber,
    indentDate,
    makerNameBrand,
    termsOfDelivery,
    totalStoreValueInput,
    gstPercentInput,
    requiredBgAmountInput,
    bgSubmissionDurationDays,
    bgValidityDurationMonths,
    paymentOnINotePercentInput,
    paymentOnCrvPercentInput,
    partialDeliveryAllowed,
    partialPaymentAllowed,
    partialDeliveryPaymentRemarks,
    earnestMoneyInput,
    consigneeLocation,
    defaultInspectionLocation,
    inspectionAuthority,
    ndcRequired,
    ndcReferenceNumber,
    ndcRemarks,
    remarks,
  });

  const applyFormState = (draft: Partial<NewProjectFormState>): void => {
    setProjectTitle(draftString(draft.projectTitle));
    setProjectNumber(draftString(draft.projectNumber));
    setClientId(draftString(draft.clientId));
    setProjectCurrency(
      draft.projectCurrency === 'USD' || draft.projectCurrency === 'OTHER' ? draft.projectCurrency : 'PKR',
    );
    setOtherCurrencyCode(draftString(draft.otherCurrencyCode));
    setPrincipalName(draftString(draft.principalName));
    setPrincipalAddress(draftString(draft.principalAddress));
    setPrincipalContactPerson(draftString(draft.principalContactPerson));
    setPrincipalEmail(draftString(draft.principalEmail));
    setPrincipalPhone(draftString(draft.principalPhone));
    setPrincipalFax(draftString(draft.principalFax));
    setPortOfShipment(draftString(draft.portOfShipment));
    setPrincipalBankName(draftString(draft.principalBankName));
    setPrincipalBankAccountNumber(draftString(draft.principalBankAccountNumber));
    setPrincipalBankIban(draftString(draft.principalBankIban));
    setPrincipalBankAccountTitle(draftString(draft.principalBankAccountTitle));
    setPrincipalBankSwiftCode(draftString(draft.principalBankSwiftCode));
    setPrincipalBankAddress(draftString(draft.principalBankAddress));
    setPrincipalBankEmail(draftString(draft.principalBankEmail));
    setPrincipalBankPhone(draftString(draft.principalBankPhone));
    setPrincipalBankFax(draftString(draft.principalBankFax));
    setDfaReceivedDate(draftString(draft.dfaReceivedDate) || todayPktIsoDate());
    setContractSignDate(draftString(draft.contractSignDate));
    setDeliveryDurationMonths(draftString(draft.deliveryDurationMonths));
    setGracePeriodDays(draftString(draft.gracePeriodDays) || '15');
    setWarrantyPeriodMonths(draftString(draft.warrantyPeriodMonths));
    setOrigin(draftString(draft.origin));
    setIndentNumber(draftString(draft.indentNumber));
    setIndentDate(draftString(draft.indentDate));
    setMakerNameBrand(draftString(draft.makerNameBrand));
    setTermsOfDelivery(draftString(draft.termsOfDelivery) as '' | ProjectTermsOfDelivery);
    setTotalStoreValueInput(draftString(draft.totalStoreValueInput));
    setGstPercentInput(draftString(draft.gstPercentInput));
    setRequiredBgAmountInput(draftString(draft.requiredBgAmountInput));
    setBgSubmissionDurationDays(draftString(draft.bgSubmissionDurationDays));
    setBgValidityDurationMonths(draftString(draft.bgValidityDurationMonths));
    setPaymentOnINotePercentInput(draftString(draft.paymentOnINotePercentInput));
    setPaymentOnCrvPercentInput(draftString(draft.paymentOnCrvPercentInput));
    setPartialDeliveryAllowed(draftBoolean(draft.partialDeliveryAllowed));
    setPartialPaymentAllowed(draftBoolean(draft.partialPaymentAllowed));
    setPartialDeliveryPaymentRemarks(draftString(draft.partialDeliveryPaymentRemarks));
    setEarnestMoneyInput(draftString(draft.earnestMoneyInput));
    setConsigneeLocation(draftString(draft.consigneeLocation));
    setDefaultInspectionLocation(
      draft.defaultInspectionLocation === 'CLIENT_PREMISES' ||
      draft.defaultInspectionLocation === 'THIRD_PARTY_PREMISES' ||
      draft.defaultInspectionLocation === 'TBD'
        ? draft.defaultInspectionLocation
        : 'OUR_PREMISES',
    );
    setInspectionAuthority(draftString(draft.inspectionAuthority));
    setNdcRequired(draftBoolean(draft.ndcRequired));
    setNdcReferenceNumber(draftString(draft.ndcReferenceNumber));
    setNdcRemarks(draftString(draft.ndcRemarks));
    setRemarks(draftString(draft.remarks));
  };

  useEffect(() => {
    if (!open) {
      setProjectTitle('');
      setProjectNumber('');
      setClientId('');
      setProjectCurrency('PKR');
      setOtherCurrencyCode('');
      setPrincipalName('');
      setPrincipalAddress('');
      setPrincipalContactPerson('');
      setPrincipalEmail('');
      setPrincipalPhone('');
      setPrincipalFax('');
      setPortOfShipment('');
      setPrincipalBankName('');
      setPrincipalBankAccountNumber('');
      setPrincipalBankIban('');
      setPrincipalBankAccountTitle('');
      setPrincipalBankSwiftCode('');
      setPrincipalBankAddress('');
      setPrincipalBankEmail('');
      setPrincipalBankPhone('');
      setPrincipalBankFax('');
      setDfaReceivedDate(todayPktIsoDate());
      setContractSignDate('');
      setDeliveryDurationMonths('');
      setGracePeriodDays('15');
      setWarrantyPeriodMonths('');
      setOrigin('');
      setIndentNumber('');
      setIndentDate('');
      setMakerNameBrand('');
      setTermsOfDelivery('');
      setTotalStoreValueInput('');
      setGstPercentInput('');
      setRequiredBgAmountInput('');
      setBgSubmissionDurationDays('');
      setBgValidityDurationMonths('');
      setPaymentOnINotePercentInput('');
      setPaymentOnCrvPercentInput('');
      setPartialDeliveryAllowed(false);
      setPartialPaymentAllowed(false);
      setPartialDeliveryPaymentRemarks('');
      setEarnestMoneyInput('');
      setConsigneeLocation('');
      setDefaultInspectionLocation('OUR_PREMISES');
      setInspectionAuthority('');
      setNdcRequired(false);
      setNdcReferenceNumber('');
      setNdcRemarks('');
      setRemarks('');
      createProject.reset();
    }
  }, [open, createProject]);

  useEffect(() => {
    if (projectCurrency !== 'PKR' && termsOfDelivery === 'FOR') {
      setTermsOfDelivery('');
    }
  }, [projectCurrency, termsOfDelivery]);

  useEffect(() => {
    if (clientId === '' && clients.data && clients.data.length > 0) {
      const firstActive = clients.data.find((c) => c.isActive);
      if (firstActive) setClientId(firstActive.id);
    }
  }, [clients.data, clientId]);

  const commercialPreview = useMemo(() => {
    const totalStoreValuePaisa = totalStoreValueInput.trim()
      ? parseRupeeInputToPaisa(totalStoreValueInput)
      : null;
    const gstRateBps = gstPercentInput.trim() ? parsePercentInputToBps(gstPercentInput) : null;
    const gstAmountPaisa = deriveGstAmountPaisa(totalStoreValuePaisa, gstRateBps);
    const declaredTotalContractValuePaisa = deriveDeclaredTotalContractValuePaisa(
      totalStoreValuePaisa,
      gstRateBps,
    );
    const paymentOnINoteBps = paymentOnINotePercentInput.trim()
      ? parsePercentInputToBps(paymentOnINotePercentInput)
      : null;
    const paymentOnCrvBps = paymentOnCrvPercentInput.trim()
      ? parsePercentInputToBps(paymentOnCrvPercentInput)
      : null;
    const paymentTotalBps =
      paymentOnINoteBps !== null && paymentOnCrvBps !== null
        ? paymentOnINoteBps + paymentOnCrvBps
        : null;
    return {
      totalStoreValuePaisa,
      gstRateBps,
      gstAmountPaisa,
      declaredTotalContractValuePaisa,
      paymentOnINoteBps,
      paymentOnCrvBps,
      paymentTotalBps,
    };
  }, [
    gstPercentInput,
    paymentOnCrvPercentInput,
    paymentOnINotePercentInput,
    totalStoreValueInput,
  ]);

  const invalidCurrencyInput = Boolean(
    projectCurrency === 'OTHER' && otherCurrencyCode.trim() === '',
  );
  const invalidForeignDetails = Boolean(
    projectCurrency !== 'PKR' && !hasCompleteForeignProjectDetails(currentFormState()),
  );
  const incotermOptions = useMemo(() => {
    const codes = incoterms.data?.length
      ? incoterms.data.filter((term) => term.isActive).map((term) => term.code)
      : [...TermsOfDelivery];
    return codes.filter((code, index) => codes.indexOf(code) === index).filter((code) => {
      return projectCurrency === 'PKR' || code !== 'FOR';
    });
  }, [incoterms.data, projectCurrency]);

  const invalidTermsOfDelivery = projectCurrency !== 'PKR' && termsOfDelivery === 'FOR';

  const invalidCommercialInput = Boolean(
    (totalStoreValueInput.trim() && commercialPreview.totalStoreValuePaisa === null) ||
      (gstPercentInput.trim() && commercialPreview.gstRateBps === null) ||
      (requiredBgAmountInput.trim() && parseRupeeInputToPaisa(requiredBgAmountInput) === null) ||
      (bgSubmissionDurationDays.trim() && !Number.isInteger(Number(bgSubmissionDurationDays))) ||
      (bgValidityDurationMonths.trim() && !Number.isInteger(Number(bgValidityDurationMonths))) ||
      (paymentOnINotePercentInput.trim() && commercialPreview.paymentOnINoteBps === null) ||
      (paymentOnCrvPercentInput.trim() && commercialPreview.paymentOnCrvBps === null) ||
      ((paymentOnINotePercentInput.trim() || paymentOnCrvPercentInput.trim()) &&
        commercialPreview.paymentTotalBps !== 10_000),
  );

  const saveCurrentDraft = async (): Promise<void> => {
    await saveDraft.mutateAsync({
      draftType: 'PROJECT_CREATE',
      payloadJson: { form: currentFormState() as unknown as Record<string, unknown> },
    });
  };

  const resumeLatestDraft = (): void => {
    if (!latestProjectDraft) return;
    const payload = latestProjectDraft.payloadJson;
    if (isProjectCreateDraftPayload(payload)) {
      applyFormState(payload.form);
    }
  };

  const discardLatestDraft = (): void => {
    if (!latestProjectDraft) return;
    discardDraft.mutate({ id: latestProjectDraft.id, rowVersion: latestProjectDraft.rowVersion });
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const payload = buildCreateProjectPayload(
      {
        projectTitle,
        projectNumber,
        clientId,
        projectCurrency,
        otherCurrencyCode,
        principalName,
        principalAddress,
        principalContactPerson,
        principalEmail,
        principalPhone,
        principalFax,
        portOfShipment,
        principalBankName,
        principalBankAccountNumber,
        principalBankIban,
        principalBankAccountTitle,
        principalBankSwiftCode,
        principalBankAddress,
        principalBankEmail,
        principalBankPhone,
        principalBankFax,
        dfaReceivedDate,
        contractSignDate,
        deliveryDurationMonths,
        gracePeriodDays,
        warrantyPeriodMonths,
        origin,
        indentNumber,
        indentDate,
        makerNameBrand,
        termsOfDelivery,
        totalStoreValueInput,
        gstPercentInput,
        requiredBgAmountInput,
        bgSubmissionDurationDays,
        bgValidityDurationMonths,
        paymentOnINotePercentInput,
        paymentOnCrvPercentInput,
        partialDeliveryAllowed,
        partialPaymentAllowed,
        partialDeliveryPaymentRemarks,
        earnestMoneyInput,
        consigneeLocation,
        defaultInspectionLocation,
        inspectionAuthority,
        ndcRequired,
        ndcReferenceNumber,
        ndcRemarks,
        remarks,
      },
      { name: normaliseName, projectNumber: normaliseProjectNumber },
    );
    if (!payload) return;
    createProject.mutate(payload, {
      onSuccess: (project) => {
        if (latestProjectDraft) {
          discardDraft.mutate({ id: latestProjectDraft.id, rowVersion: latestProjectDraft.rowVersion });
        }
        onClose();
        void navigate({
          to: '/projects/$projectId',
          params: { projectId: project.id },
        });
      },
    });
  };

  const isDirty = Boolean(
    projectTitle ||
      projectNumber ||
      projectCurrency !== 'PKR' ||
      otherCurrencyCode ||
      principalName ||
      principalAddress ||
      principalContactPerson ||
      principalEmail ||
      principalPhone ||
      principalFax ||
      portOfShipment ||
      principalBankName ||
      principalBankAccountNumber ||
      principalBankIban ||
      principalBankAccountTitle ||
      principalBankSwiftCode ||
      principalBankAddress ||
      principalBankEmail ||
      principalBankPhone ||
      principalBankFax ||
      contractSignDate ||
      deliveryDurationMonths ||
      warrantyPeriodMonths ||
      origin ||
      indentNumber ||
      indentDate ||
      makerNameBrand ||
      termsOfDelivery ||
      totalStoreValueInput ||
      gstPercentInput ||
      requiredBgAmountInput ||
      bgSubmissionDurationDays ||
      bgValidityDurationMonths ||
      paymentOnINotePercentInput ||
      paymentOnCrvPercentInput ||
      partialDeliveryAllowed ||
      partialPaymentAllowed ||
      partialDeliveryPaymentRemarks ||
      earnestMoneyInput ||
      consigneeLocation ||
      inspectionAuthority ||
      ndcRequired ||
      ndcReferenceNumber ||
      ndcRemarks ||
      remarks,
  );

  const canSubmit = Boolean(
    projectTitle.trim() &&
      projectNumber.trim() &&
      clientId &&
      dfaReceivedDate &&
      !invalidCurrencyInput &&
      !invalidForeignDetails &&
      !invalidTermsOfDelivery &&
      !invalidCommercialInput,
  );

  const parsedDeliveryDurationMonths = Number(deliveryDurationMonths);
  const effectiveDeliveryTargetPreview =
    contractSignDate &&
    Number.isInteger(parsedDeliveryDurationMonths) &&
    parsedDeliveryDurationMonths > 0
      ? addMonthsClamped(contractSignDate, parsedDeliveryDurationMonths)
      : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New project"
      description="Create a project from DFA receipt, contract timing, and commercial terms."
      size="lg"
      lockBackdrop={isDirty && !createProject.isSuccess}
      footer={
        <>
          <Button variant="tertiary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            type="button"
            onClick={() => void saveCurrentDraft()}
            loading={saveDraft.isPending}
            disabled={!isDirty}
          >
            Save draft
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="new-project-form"
            loading={createProject.isPending}
            disabled={!canSubmit}
          >
            Create project
          </Button>
        </>
      }
    >
      <form id="new-project-form" onSubmit={onSubmit} className="space-y-4">
        {createProject.error ? (
          <Alert tone="danger">{createProject.error.message}</Alert>
        ) : null}
        {saveDraft.error ? <Alert tone="danger">{saveDraft.error.message}</Alert> : null}
        {discardDraft.error ? <Alert tone="danger">{discardDraft.error.message}</Alert> : null}
        {latestProjectDraft ? (
          <Alert tone="info">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span>A saved project draft is available.</span>
              <div className="flex gap-2">
                <Button variant="secondary" size="compact" type="button" onClick={resumeLatestDraft}>
                  Resume draft
                </Button>
                <Button
                  variant="tertiary"
                  size="compact"
                  type="button"
                  loading={discardDraft.isPending}
                  onClick={discardLatestDraft}
                >
                  Discard
                </Button>
              </div>
            </div>
          </Alert>
        ) : null}
        {invalidCurrencyInput ? (
          <Alert tone="warning">Enter the foreign currency code/name when using Other foreign currency.</Alert>
        ) : null}
        {invalidForeignDetails ? (
          <Alert tone="warning">
            Complete all foreign principal and principal bank details before creating a non-PKR project.
          </Alert>
        ) : null}
        {invalidTermsOfDelivery ? (
          <Alert tone="warning">FOR is allowed only for PKR projects. Select another incoterm for foreign currency.</Alert>
        ) : null}
        {invalidCommercialInput ? (
          <Alert tone="warning">
            Check the commercial terms: percentages must be valid and payment terms must total 100%.
          </Alert>
        ) : null}

        <section className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-tertiary">
            Project identity
          </h3>
          <FormField label="Project title" required htmlFor="title">
            <Input
              id="title"
              value={projectTitle}
              onChange={(event) => setProjectTitle(event.target.value)}
              placeholder="e.g. Radar Modernisation Phase II"
            />
          </FormField>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Project number" required htmlFor="number">
              <Input
                id="number"
                value={projectNumber}
                onChange={(event) => setProjectNumber(event.target.value)}
                placeholder="e.g. AF-2026-101"
                className="font-mono tabular-nums"
              />
            </FormField>
            <FormField label="Client" required htmlFor="client">
              {clients.isLoading ? (
                <div className="flex h-9 items-center gap-2 text-sm text-secondary">
                  <Spinner className="h-3 w-3" /> Loading clients…
                </div>
              ) : (
                <SearchableSelect
                  id="client"
                  items={(clients.data ?? [])
                    .filter((c) => c.isActive)
                    .map((c) => ({ value: c.id, label: c.name }))}
                  value={clientId || null}
                  onChange={(v) => setClientId(v ?? '')}
                  placeholder="Select a client…"
                />
              )}
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Project currency" required htmlFor="project-currency">
              <Select
                id="project-currency"
                value={projectCurrency}
                onChange={(event) => setProjectCurrency(event.target.value as ProjectCurrencyCode)}
              >
                <option value="PKR">PKR</option>
                <option value="USD">USD</option>
                <option value="OTHER">Other foreign currency</option>
              </Select>
            </FormField>
            {projectCurrency === 'OTHER' ? (
              <FormField label="Other currency code/name" required htmlFor="other-currency-code">
                <Input
                  id="other-currency-code"
                  value={otherCurrencyCode}
                  onChange={(event) => setOtherCurrencyCode(event.target.value)}
                  placeholder="e.g. EUR"
                  className="uppercase"
                />
              </FormField>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="DFA received" required htmlFor="dfa-received">
              <DmyDateInput
                id="dfa-received"
                value={dfaReceivedDate}
                maxDate={todayPktIsoDate()}
                onChange={setDfaReceivedDate}
              />
            </FormField>
            <FormField
              label="Contract sign date"
              htmlFor="contract-sign-date"
              helpText="Optional — starts delivery-duration clock"
            >
              <DmyDateInput
                id="contract-sign-date"
                value={contractSignDate}
                maxDate={todayPktIsoDate()}
                onChange={setContractSignDate}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField
              label="Delivery duration (months)"
              htmlFor="delivery-duration-months"
              helpText={
                effectiveDeliveryTargetPreview
                  ? `Effective target: ${effectiveDeliveryTargetPreview}`
                  : 'Months from contract sign date to target'
              }
            >
              <Input
                id="delivery-duration-months"
                type="number"
                min={1}
                max={240}
                value={deliveryDurationMonths}
                onChange={(event) => setDeliveryDurationMonths(event.target.value)}
                className="tabular-nums"
              />
            </FormField>
            <FormField
              label="Grace period (days)"
              htmlFor="grace-days"
              helpText="Days past effective target before considered overdue"
            >
              <Input
                id="grace-days"
                type="number"
                min={0}
                max={365}
                value={gracePeriodDays}
                onChange={(event) => setGracePeriodDays(event.target.value)}
                className="tabular-nums"
              />
            </FormField>
          </div>
        </section>

        <details className="rounded-md border border-border-default p-3" open>
          <summary className="cursor-pointer text-sm font-semibold text-primary">
            Contract / indent details
          </summary>
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Origin" htmlFor="origin">
                <Input
                  id="origin"
                  value={origin}
                  onChange={(event) => setOrigin(event.target.value)}
                  placeholder="e.g. Local, Import, Germany"
                />
              </FormField>
              <FormField label="Indent number" htmlFor="indent-number">
                <Input
                  id="indent-number"
                  value={indentNumber}
                  onChange={(event) => setIndentNumber(event.target.value)}
                  placeholder="e.g. IND/AF/2026-044"
                />
              </FormField>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Indent date" htmlFor="indent-date">
                <DmyDateInput
                  id="indent-date"
                  value={indentDate}
                  maxDate={todayPktIsoDate()}
                  onChange={setIndentDate}
                />
              </FormField>
              <FormField label="Terms of delivery" htmlFor="terms-of-delivery">
                <Select
                  id="terms-of-delivery"
                  value={termsOfDelivery}
                  onChange={(event) =>
                    setTermsOfDelivery(event.target.value as '' | ProjectTermsOfDelivery)
                  }
                >
                  <option value="">Select terms…</option>
                  {incotermOptions.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </Select>
              </FormField>
            </div>
            <FormField label="Maker's name / brand" htmlFor="maker-name-brand">
              <Input
                id="maker-name-brand"
                value={makerNameBrand}
                onChange={(event) => setMakerNameBrand(event.target.value)}
                placeholder="e.g. Thales, HBL Power, OEM name"
              />
            </FormField>

            {projectCurrency !== 'PKR' ? (
              <div className="space-y-3 rounded-md border border-border-default p-3">
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-tertiary">
                    Foreign principal details
                  </h4>
                  <p className="mt-0.5 text-xs text-secondary">
                    Shown for USD and other foreign currency projects.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField label="Principal name" required htmlFor="principal-name">
                    <Input id="principal-name" value={principalName} onChange={(event) => setPrincipalName(event.target.value)} />
                  </FormField>
                  <FormField label="Contact person" required htmlFor="principal-contact-person">
                    <Input id="principal-contact-person" value={principalContactPerson} onChange={(event) => setPrincipalContactPerson(event.target.value)} />
                  </FormField>
                </div>
                <FormField label="Principal address" required htmlFor="principal-address">
                  <Textarea id="principal-address" value={principalAddress} onChange={(event) => setPrincipalAddress(event.target.value)} rows={2} />
                </FormField>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField label="Email" required htmlFor="principal-email">
                    <Input id="principal-email" type="email" value={principalEmail} onChange={(event) => setPrincipalEmail(event.target.value)} />
                  </FormField>
                  <FormField label="Phone" required htmlFor="principal-phone">
                    <Input id="principal-phone" value={principalPhone} onChange={(event) => setPrincipalPhone(event.target.value)} />
                  </FormField>
                  <FormField label="Fax" required htmlFor="principal-fax">
                    <Input id="principal-fax" value={principalFax} onChange={(event) => setPrincipalFax(event.target.value)} />
                  </FormField>
                  <FormField label="Port of shipment" required htmlFor="port-of-shipment">
                    <Input id="port-of-shipment" value={portOfShipment} onChange={(event) => setPortOfShipment(event.target.value)} />
                  </FormField>
                </div>
              </div>
            ) : null}
          </div>
        </details>

        <details className="rounded-md border border-border-default p-3" open>
          <summary className="cursor-pointer text-sm font-semibold text-primary">
            Commercial / BG terms
          </summary>
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Total store value" htmlFor="total-store-value" helpText="Amount in rupees">
                <Input
                  id="total-store-value"
                  inputMode="decimal"
                  value={totalStoreValueInput}
                  onChange={(event) => setTotalStoreValueInput(event.target.value)}
                  placeholder="e.g. 10,000,000"
                  className="tabular-nums"
                />
              </FormField>
              <FormField label="GST %" htmlFor="gst-percent" helpText="Example: 18 or 17.5">
                <Input
                  id="gst-percent"
                  inputMode="decimal"
                  value={gstPercentInput}
                  onChange={(event) => setGstPercentInput(event.target.value)}
                  placeholder="e.g. 18"
                  className="tabular-nums"
                />
              </FormField>
            </div>
            <div className="grid grid-cols-1 gap-3 rounded-md bg-subtle p-3 text-sm sm:grid-cols-2">
              <Preview label="GST amount" value={formatMaybeCurrency(commercialPreview.gstAmountPaisa)} />
              <Preview
                label="Total contract value"
                value={formatMaybeCurrency(commercialPreview.declaredTotalContractValuePaisa)}
              />
            </div>
            {projectCurrency !== 'PKR' ? (
              <div className="space-y-3 rounded-md border border-border-default p-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-tertiary">
                  Principal bank details
                </h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField label="Bank name" required htmlFor="principal-bank-name">
                    <Input id="principal-bank-name" value={principalBankName} onChange={(event) => setPrincipalBankName(event.target.value)} />
                  </FormField>
                  <FormField label="Account title" required htmlFor="principal-bank-account-title">
                    <Input id="principal-bank-account-title" value={principalBankAccountTitle} onChange={(event) => setPrincipalBankAccountTitle(event.target.value)} />
                  </FormField>
                  <FormField label="Account number" required htmlFor="principal-bank-account-number">
                    <Input id="principal-bank-account-number" value={principalBankAccountNumber} onChange={(event) => setPrincipalBankAccountNumber(event.target.value)} />
                  </FormField>
                  <FormField label="IBAN" required htmlFor="principal-bank-iban">
                    <Input id="principal-bank-iban" value={principalBankIban} onChange={(event) => setPrincipalBankIban(event.target.value)} />
                  </FormField>
                  <FormField label="SWIFT code" required htmlFor="principal-bank-swift-code">
                    <Input id="principal-bank-swift-code" value={principalBankSwiftCode} onChange={(event) => setPrincipalBankSwiftCode(event.target.value)} />
                  </FormField>
                  <FormField label="Bank email" required htmlFor="principal-bank-email">
                    <Input id="principal-bank-email" type="email" value={principalBankEmail} onChange={(event) => setPrincipalBankEmail(event.target.value)} />
                  </FormField>
                  <FormField label="Bank phone" required htmlFor="principal-bank-phone">
                    <Input id="principal-bank-phone" value={principalBankPhone} onChange={(event) => setPrincipalBankPhone(event.target.value)} />
                  </FormField>
                  <FormField label="Bank fax" required htmlFor="principal-bank-fax">
                    <Input id="principal-bank-fax" value={principalBankFax} onChange={(event) => setPrincipalBankFax(event.target.value)} />
                  </FormField>
                </div>
                <FormField label="Bank address" required htmlFor="principal-bank-address">
                  <Textarea id="principal-bank-address" value={principalBankAddress} onChange={(event) => setPrincipalBankAddress(event.target.value)} rows={2} />
                </FormField>
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormField label="BG amount" htmlFor="required-bg-amount" helpText="Amount in rupees">
                <Input
                  id="required-bg-amount"
                  inputMode="decimal"
                  value={requiredBgAmountInput}
                  onChange={(event) => setRequiredBgAmountInput(event.target.value)}
                  placeholder="e.g. 1,180,000"
                  className="tabular-nums"
                />
              </FormField>
              <FormField label="BG submission days" htmlFor="bg-submission-days">
                <Input
                  id="bg-submission-days"
                  type="number"
                  min={0}
                  max={365}
                  value={bgSubmissionDurationDays}
                  onChange={(event) => setBgSubmissionDurationDays(event.target.value)}
                  className="tabular-nums"
                />
              </FormField>
              <FormField label="BG validity months" htmlFor="bg-validity-months">
                <Input
                  id="bg-validity-months"
                  type="number"
                  min={1}
                  max={240}
                  value={bgValidityDurationMonths}
                  onChange={(event) => setBgValidityDurationMonths(event.target.value)}
                  className="tabular-nums"
                />
              </FormField>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Payment on I-Note %" htmlFor="payment-i-note">
                <Input
                  id="payment-i-note"
                  inputMode="decimal"
                  value={paymentOnINotePercentInput}
                  onChange={(event) => setPaymentOnINotePercentInput(event.target.value)}
                  placeholder="e.g. 80"
                  className="tabular-nums"
                />
              </FormField>
              <FormField label="Payment on CRV %" htmlFor="payment-crv">
                <Input
                  id="payment-crv"
                  inputMode="decimal"
                  value={paymentOnCrvPercentInput}
                  onChange={(event) => setPaymentOnCrvPercentInput(event.target.value)}
                  placeholder="e.g. 20"
                  className="tabular-nums"
                />
              </FormField>
            </div>
            <p
              className={`text-xs ${
                commercialPreview.paymentTotalBps === null || commercialPreview.paymentTotalBps === 10_000
                  ? 'text-secondary'
                  : 'text-danger-fg'
              }`}
            >
              Payment split:{' '}
              {commercialPreview.paymentTotalBps === null
                ? '—'
                : `${formatBpsAsPercent(commercialPreview.paymentTotalBps)} ${
                    commercialPreview.paymentTotalBps === 10_000 ? '✓' : '— must equal 100.00%'
                  }`}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex items-center gap-2 rounded-md border border-border-default p-2 text-sm text-secondary">
                <input
                  type="checkbox"
                  checked={partialDeliveryAllowed}
                  onChange={(event) => setPartialDeliveryAllowed(event.target.checked)}
                />
                Partial delivery allowed
              </label>
              <label className="flex items-center gap-2 rounded-md border border-border-default p-2 text-sm text-secondary">
                <input
                  type="checkbox"
                  checked={partialPaymentAllowed}
                  onChange={(event) => setPartialPaymentAllowed(event.target.checked)}
                />
                Partial payment allowed
              </label>
            </div>
            <FormField label="Partial delivery/payment remarks" htmlFor="partial-delivery-payment-remarks">
              <Textarea
                id="partial-delivery-payment-remarks"
                value={partialDeliveryPaymentRemarks}
                onChange={(event) => setPartialDeliveryPaymentRemarks(event.target.value)}
                rows={2}
              />
            </FormField>
          </div>
        </details>

        <details className="rounded-md border border-border-default p-3">
          <summary className="cursor-pointer text-sm font-semibold text-primary">
            Logistics / notes
          </summary>
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label="Warranty (months)" htmlFor="warranty-months">
                <Input
                  id="warranty-months"
                  type="number"
                  min={0}
                  max={240}
                  value={warrantyPeriodMonths}
                  onChange={(event) => setWarrantyPeriodMonths(event.target.value)}
                  className="tabular-nums"
                />
              </FormField>
              <FormField label="Earnest money" htmlFor="earnest-money" helpText="Amount in rupees">
                <Input
                  id="earnest-money"
                  type="text"
                  inputMode="decimal"
                  value={earnestMoneyInput}
                  onChange={(event) => setEarnestMoneyInput(event.target.value)}
                  placeholder="e.g. 50,000"
                  className="tabular-nums"
                />
              </FormField>
            </div>
            <FormField label="Consignee location" htmlFor="consignee-location">
              <Input
                id="consignee-location"
                type="text"
                value={consigneeLocation}
                onChange={(event) => setConsigneeLocation(event.target.value)}
                placeholder="e.g. Air Force Base"
              />
            </FormField>
            <FormField
              label="Default inspection location"
              helpText="Pre-fills inspection events on this project. Each event can override."
            >
              <div
                role="radiogroup"
                aria-label="Default inspection location"
                className="inline-flex rounded-md border border-border-default p-0.5"
              >
                <InspectionLocationPill
                  label="Firm’s premises"
                  active={defaultInspectionLocation === 'OUR_PREMISES'}
                  onClick={() => setDefaultInspectionLocation('OUR_PREMISES')}
                />
                <InspectionLocationPill
                  label="Client premises"
                  active={defaultInspectionLocation === 'CLIENT_PREMISES'}
                  onClick={() => setDefaultInspectionLocation('CLIENT_PREMISES')}
                />
                <InspectionLocationPill
                  label="Third party premises"
                  active={defaultInspectionLocation === 'THIRD_PARTY_PREMISES'}
                  onClick={() => setDefaultInspectionLocation('THIRD_PARTY_PREMISES')}
                />
                <InspectionLocationPill
                  label="TBD"
                  active={defaultInspectionLocation === 'TBD'}
                  onClick={() => setDefaultInspectionLocation('TBD')}
                />
              </div>
            </FormField>
            <FormField label="Inspection authority" htmlFor="inspection-authority">
              <Input
                id="inspection-authority"
                type="text"
                value={inspectionAuthority}
                onChange={(event) => setInspectionAuthority(event.target.value)}
                placeholder="e.g. DQA"
              />
            </FormField>
            <div className="space-y-3 rounded-md border border-border-default p-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-tertiary">
                NDC details
              </h4>
              <label className="flex items-center gap-2 text-sm text-secondary">
                <input
                  type="checkbox"
                  checked={ndcRequired}
                  onChange={(event) => setNdcRequired(event.target.checked)}
                />
                NDC required
              </label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField label="NDC reference number" htmlFor="ndc-reference-number">
                  <Input
                    id="ndc-reference-number"
                    value={ndcReferenceNumber}
                    onChange={(event) => setNdcReferenceNumber(event.target.value)}
                  />
                </FormField>
                <FormField label="NDC remarks" htmlFor="ndc-remarks">
                  <Input
                    id="ndc-remarks"
                    value={ndcRemarks}
                    onChange={(event) => setNdcRemarks(event.target.value)}
                  />
                </FormField>
              </div>
            </div>
            <FormField label="Remarks" htmlFor="remarks" helpText="Optional notes for the team">
              <Textarea
                id="remarks"
                value={remarks}
                onChange={(event) => setRemarks(event.target.value)}
                rows={2}
              />
            </FormField>
          </div>
        </details>
      </form>
    </Modal>
  );
}

function formatMaybeCurrency(value: number | null): string {
  return value === null ? '—' : formatCurrency(value);
}

function Preview({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-primary tabular-nums">{value}</div>
    </div>
  );
}

function InspectionLocationPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
        active ? 'bg-accent text-accent-fg' : 'text-secondary hover:bg-subtle'
      }`}
    >
      {label}
    </button>
  );
}
